import React, { useEffect, useMemo, useState } from 'react';

import { CartType } from '@/constants/cart';
import constants from '@/constants/const';
import { StatusEnum } from '@/types/cart';
import { ProductType, ShopMoreType } from '@/types/product';
import { CartItemV2, PerchaseItemReq } from '@/types/so';
import { PositionInfoT2Param } from '@/types/track';
import callAppFunction from '@/utils/bridge';
import { getOrderToken, getPreOrderId, getUniqueProductId } from '@/utils/business';
import { getCookie, isAppSupportPurchaseStatusNotify as isAppSupportPurchaseStatus, isSayWeeeApp } from '@/utils/tool';

import Cartbtn from './components/Cartbtn';
import SoldOut from './components/SoldOut';
import { ChangeOtherDay, Purchased } from './components/UnavailableCartbtn';
import { Toast } from '@/components/common/CommonToast';
import dynamic from 'next/dynamic';
import { gaAddToCart } from '@/utils/ga-data-manager';

const CartConsumer = dynamic(() => import('@/components/common/CartConsumer'), {
  ssr: false
});

export interface AddCartProps {
  num?: number;
  //每次增加的数量
  count?: number;
  text?: string;
  imageUrl?: string;
  className?: string;
  data?: ProductType | CartItemV2;
  is?: ProductType;
  isNotification?: boolean;
  source?: string; // TODO source 字段 首次优先：如果一个商品已经被加入购物车设置好source，那么在后续其他页面调整其数量，并不修改source；
  referType: string;
  referValue?: string;
  deliveryDate: string;
  id2qty?: ObjectType;
  /**加购按钮是否显示文案 */
  renderCartCountText?: boolean;
  render?: any;
  onClick?: (type?, num?) => void;
  isInCart?: boolean;
  addCart?: (data: CartItemV2, count: number) => void;
  subCart?: (data: CartItemV2, count: number) => void;
  /**delivery组件 根据productId获取商品的配送日期 */
  productId?: number;
  minLimitArrowToTarget?: number;
  preSell?: boolean;
  changeDeliveryDateButtonText?: string;
  link?: string;
  onNumChange?: () => void;
  isInProductCard?: boolean;
  showTooltip?: boolean;
  positionInfoT2?: PositionInfoT2Param;
  isAppSupportPurchaseStatusNotify?: boolean;
  index?: number;
  trackDataInDialog?: Record<string, any>;
  clickTracking?: () => void;
  fetchYouMayLike?: boolean;
  useNewUpdateCartApi?: boolean; // 是否使用新的更新购物车接口 items/cart/v3
  customAddCartApi?: string; // 自定义加购接口
  isFetchSimpleOrder?: boolean;
  shopMoreType?: ShopMoreType;
  refValue?: string | null;
}

export const AddCart = ({
  imageUrl,
  isNotification = false,
  data = {},
  text,
  className,
  addCart,
  subCart,
  source,
  referType,
  referValue,
  deliveryDate,
  id2qty,
  renderCartCountText,
  onClick,
  render,
  productId,
  minLimitArrowToTarget,
  preSell,
  changeDeliveryDateButtonText,
  isInProductCard,
  showTooltip,
  positionInfoT2,
  clickTracking,
  ...rest
}: AddCartProps) => {
  const [num, setNum] = useState<number>(0);
  const min = +data?.min_order_quantity || 0;
  const goodsUsefulInfo = useMemo(() => {
    const goods: CartItemV2 = {
      product_id: data?.product_id || data?.id,
      source,
      source_store: getCookie(constants.WEEE_STORE),
      refer_type: referType,
      refer_value: referValue || '',
      delivery_date: deliveryDate,
      img: data?.img,
      title: data?.title || data?.name,
      unit_info: data?.unit_info || '',
      sold_status: data?.sold_status || 'available',
      max_order_quantity: data?.max_order_quantity || 9999,
      min_order_quantity: data?.min_order_quantity || 1,
      is_pantry: data?.is_pantry || false,
      is_alcohol: data?.is_alcohol || false,
      item_type: data?.item_type || '',
      buttonText: changeDeliveryDateButtonText,
      is_mkpl: data?.biz_type === 'seller',
      biz_type: data?.biz_type || '',
      product_key: data?.product_key ? data.product_key : undefined,
      options: data?.options ? data.options : undefined,
      positionInfoT2: positionInfoT2,
      ctx: positionInfoT2?.context,
      is_sponsored: data?.is_sponsored || void 0,
      is_manual: data?.is_manual || void 0,
      volume_price_support: data?.volume_price_support || void 0,
      volume_threshold: data?.volume_threshold || void 0
    };
    return goods;
  }, [JSON.stringify(data), referType, JSON.stringify(positionInfoT2)]);

  useEffect(() => {
    const uniqueId = getUniqueProductId(goodsUsefulInfo.product_id, goodsUsefulInfo.product_key);

    setNum(id2qty?.[uniqueId] || 0);
  }, [JSON.stringify(id2qty), goodsUsefulInfo.product_id]);

  const addCount = (e, onAdd: (num: number) => void) => {
    if (num < min) {
      setNum(min);
      addCart?.(goodsUsefulInfo, min);
      onAdd && onAdd(min);
      onClick && onClick('add', min);
    } else {
      setNum(cartQty => cartQty + 1);
      addCart?.(goodsUsefulInfo, num + 1);
      onAdd && onAdd(num + 1);
      onClick && onClick('add', num + 1);
    }

    //gtm add to cart pixel event
    gaAddToCart({
      title: goodsUsefulInfo?.title || goodsUsefulInfo?.name,
      product_id: goodsUsefulInfo?.product_id,
      quantity: goodsUsefulInfo?.volume_price_support ? goodsUsefulInfo?.volume_threshold : num < min ? min : num + 1,
      price: data?.price
    });
  };
  const subtractCount = e => {
    if (num === min) {
      setNum(0);
      subCart?.(goodsUsefulInfo, 0);
    } else {
      setNum(cartQty => cartQty - 1);
      subCart?.(goodsUsefulInfo, num - 1);
    }
  };

  if (render) {
    return render({ ...(data || {}), ...(goodsUsefulInfo || {}) }, addCart, subCart, rest?.index);
  }
  switch (goodsUsefulInfo?.sold_status) {
    case StatusEnum.available:
      return (
        <Cartbtn
          className={className}
          preSell={preSell}
          text={text}
          renderCartCountText={renderCartCountText}
          num={num}
          max={goodsUsefulInfo?.max_order_quantity}
          min={goodsUsefulInfo?.min_order_quantity}
          addCount={addCount}
          subtractCount={subtractCount}
          minLimitArrowToTarget={minLimitArrowToTarget}
          is_pantry={goodsUsefulInfo?.is_pantry}
          isAlcohol={
            goodsUsefulInfo?.item_type === CartType.alcohol || goodsUsefulInfo?.refer_type === CartType.alcohol
          }
          data={data}
          showTooltip={showTooltip}
          link={rest?.link}
          isInProductCard={isInProductCard}
        />
      );
    case StatusEnum.reach_limit:
      return <Purchased className={className} />;
    case StatusEnum.change_other_day:
      return <ChangeOtherDay item={goodsUsefulInfo} className={className} data={data} />;
    case StatusEnum.sold_out:
      return (
        <SoldOut id={goodsUsefulInfo.product_id} className={className} clickTracking={clickTracking} data={data} />
      );
    default:
      return <div></div>;
  }
};

// 通知APP更新购物车数据
const notifyAppToUpdate = (params?: PerchaseItemReq[], isAppSupportPurchaseStatusNotify?: boolean) => {
  try {
    if (isSayWeeeApp()) {
      const isAppSupportPurchaseStatusNotifyVersion = isAppSupportPurchaseStatus();
      if (isAppSupportPurchaseStatusNotify && isAppSupportPurchaseStatusNotifyVersion) {
        const args = {
          products: params
        };
        callAppFunction('purchaseStatusNotify', args);
      } else {
        callAppFunction('preOrderStatusNotify', {
          pre_order_id: getPreOrderId(),
          token: getOrderToken(),
          status: 'propertyChanged'
        });
      }
    }
  } catch (error) {
    console.log('notifyAppToUpdate error', error);
  }
};
const AddCartEnhance = ({
  isInCart,
  useNewUpdateCartApi,
  isAppSupportPurchaseStatusNotify,
  onNumChange,
  trackDataInDialog,
  fetchYouMayLike,
  customAddCartApi,
  isFetchSimpleOrder,
  shopMoreType,
  refValue,
  ...rest
}: AddCartProps) => {
  const handleAddError = (res: ObjectType) => {
    Toast({
      text: res?.message,
      showIcon: false,
      position: 'center',
      className: 'text-center',
      innerClassName: '!w-auto max-w-[340px] inline-flex'
    });
  };
  return (
    <CartConsumer
      trackDataInDialog={trackDataInDialog}
      useNewUpdateCartApi={useNewUpdateCartApi}
      customAddCartApi={customAddCartApi}
      isInCart={isInCart}
      isFetchSimpleOrder={isFetchSimpleOrder}
      fetchYouMayLike={fetchYouMayLike}
      isAppSupportPurchaseStatusNotify={isAppSupportPurchaseStatusNotify}
      shopMoreType={shopMoreType}
      refValue={refValue}
      changeNum={(params, isAppSupportPurchaseStatusNotify) => {
        onNumChange?.();
        notifyAppToUpdate(params, isAppSupportPurchaseStatusNotify);
      }}
      onError={handleAddError}>
      <AddCart {...rest} />
    </CartConsumer>
  );
};

export default AddCartEnhance;
